import {
  DropDown,
  FilterContext,
  Icon,
  ModalContext,
  PaginationContext,
  SearchContext,
  SortContext,
  SortHeader,
  TableWitPagination,
  Text,
} from "dash-ui";
import {
  MarketAreaFragment,
  useGetMarketAreasQuery,
} from "gql/generated.graphql";
import { useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MarketAreaModals } from "../MarketAreasPage.constants";
import { useFlexLayout } from "react-table";

const MarketAreasTable = () => {
  const { t } = useTranslation();
  const { t: tLabels } = useTranslation("common", { keyPrefix: "labels" });
  const {
    setTotal,
    state: paginationState,
    getRefetchProps,
  } = useContext(PaginationContext);
  const { searchSlug } = useContext(SearchContext);
  const { state: sort } = useContext(SortContext);
  const { skip, limit } = getRefetchProps(paginationState);
  const { toggleModal, setPayload } = useContext(ModalContext);
  const searchText = searchSlug.length > 2 ? searchSlug : null;
  const { state: filterState } = useContext(FilterContext);

  const {
    data: result,
    refetch,
    loading,
  } = useGetMarketAreasQuery({
    variables: {
      sort: [sort],
      pagination: { skip, limit },
      filter: {
        stateNames: filterState?.states,
        searchText,
      },
    },
    fetchPolicy: "network-only",
  });

  const { data, total } = useMemo(
    () => ({
      data: result?.getMarketAreas?.collection || [],
      total: result?.getMarketAreas?.totalCount || 0,
    }),
    [result]
  );

  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    setTotal(total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  const columns = useMemo(
    () => [
      {
        Header: () => (
          <SortHeader accessor="name" isDefault sortOrder={1}>
            {tLabels`name`}
          </SortHeader>
        ),
        accessor: "name",
      },
      {
        Header: () => (
          <SortHeader accessor="state.name">{tLabels`state`}</SortHeader>
        ),
        accessor: "state.name",
      },
      {
        Header: () => <Text whiteSpace="nowrap">{tLabels`actions`}</Text>,
        accessor: "_id",
        width: 20,
        Cell: ({
          row: { original },
        }: {
          row: { original: MarketAreaFragment };
        }) => (
          <DropDown
            data-testid="actions"
            placementHorizontal="left"
            Anchor={() => <Icon name="dots" size={20} />}
            items={[
              {
                key: "edit",
                icon: "edit",
                w: 170,
                children: t("common:labels.viewEditAction"),
                contentCentered: false,
                onClick: () => {
                  toggleModal(true, MarketAreaModals.SAVE_MARKET_AREA_MODAL);
                  setPayload({ mode: "update", marketArea: original });
                },
              },
              {
                key: "close",
                icon: "close",
                w: 170,
                children: t("common:labels.remove"),
                contentCentered: false,
                onClick: () => {
                  toggleModal(true, MarketAreaModals.DELETE_MARKET_AREA_MODAL);
                  setPayload({
                    marketAreaId: original._id,
                    marketAreaName: original.name,
                  });
                },
              },
            ]}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, tLabels]
  );

  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    refetch({
      pagination: { skip, limit },
      sort: [sort],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, limit, sort]);

  const labelToShow = !loading ? t(`tables:marketAreas.placeholder`) || "" : "";

  return (
    <>
      <TableWitPagination
        w="100%"
        tableProps={{
          data,
          columns,
          loading,
          maxHeight: "calc(100vh - 350px)",
          stickyHeader: true,
          layouts: [useFlexLayout],
          placeholderProps: {
            label: labelToShow,
          },
        }}
        paginationProps={{
          label: t("tables:marketAreas.paginationLabel"),
          previousButtonLabel: t("common:labels.previous"),
          nextButtonLabel: t("common:labels.next"),
        }}
      />
    </>
  );
};

export default MarketAreasTable;
